/* membersadmin.jsx — RBAC-05 "Members & access" admin sub-screen.
   Ported from Viper's rbac-ui.jsx MembersAdmin, REWIRED from the window.MAV.rbac
   mock to the real /api/org/members API. Loads before admin.jsx (like
   costcontrols.jsx) and registers window.MembersAdmin, which admin.jsx renders
   under the gated "Members & access" tab.

   Authority is the SERVER's: each row carries a server-computed `canManage` flag
   (the UI never re-derives §6), and every mutation round-trips through the
   member_scope_ok DEFINER fn. The UI deliberately surfaces the widened lead/PM
   surface — which is exactly why a UI-only gate would be a hole.

   TODO RBAC-07: fold these /api/org/members calls into GooseAPI (lib/api.js, HOT)
   and source the viewer (orgTier/memberships) from the shared /me adapter instead
   of a raw fetch here. */

const { useState: useMemState, useEffect: useMemEffect } = React;

const SEAT_LABEL = { team_lead: 'Team lead', dev: 'Dev', sme: 'SME', admin: 'Admin' };  // ORG-01 seats
const seatChipClass = (role) => ({ team_lead: 'rc-lead', dev: 'rc-builder', sme: 'rc-qa', admin: 'rc-admin' }[role] || 'rc-builder');

// Thin stopgap fetch wrapper for /api/org/members (RBAC-07 moves this into GooseAPI).
async function memFetch(path, opts) {
  // TODO RBAC-07: GooseAPI.members.*  — direct fetch is the sanctioned RBAC-05 stopgap.
  const res = await fetch('/api/org/members' + path, {
    method: (opts && opts.method) || 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: opts && opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || ('Request failed (' + res.status + ')'));
  return data;
}

// ORG-02: same stopgap shape for /api/assignments (folds into GooseAPI with RBAC-07).
async function assnFetch(path, opts) {
  const res = await fetch('/api/assignments' + path, {
    method: (opts && opts.method) || 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: opts && opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || ('Request failed (' + res.status + ')'));
  return data;
}

function MembersAdmin() {
  const [me, setMe] = useMemState(null);          // {orgTier, memberships:[{workspaceId,name,role}], activeWorkspaceId}
  const [wsId, setWsId] = useMemState(null);
  const [data, setData] = useMemState(null);       // {members, workspaceId, viewerScope}
  const [loading, setLoading] = useMemState(true);
  const [err, setErr] = useMemState(null);
  const [adding, setAdding] = useMemState(false);
  const [toast, setToast] = useMemState(null);
  const [busy, setBusy] = useMemState(null);       // userId mid-mutation
  const [resetReqs, setResetReqs] = useMemState([]); // AUTH-02: open forgot-password requests

  // Load the viewer once. TODO RBAC-07: source from the shared /me adapter.
  useMemEffect(() => {
    let alive = true;
    (window.GooseAPI && window.GooseAPI.me ? window.GooseAPI.me() : Promise.reject(new Error('offline')))
      .then((m) => {
        if (!alive || !m) return;
        setMe(m);
        const first = (m.memberships && m.memberships[0] && m.memberships[0].workspaceId) || null;
        setWsId(m.activeWorkspaceId || first);
      })
      .catch(() => { if (alive) { setErr('Could not load your account.'); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const load = (ws) => {
    setLoading(true); setErr(null);
    memFetch('?workspaceId=' + encodeURIComponent(ws))
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setErr(e.message); setLoading(false); });
    // the reset queue is a manager surface — a 403 for a plain member is expected, not an error
    memFetch('/reset-requests?workspaceId=' + encodeURIComponent(ws))
      .then((d) => setResetReqs(d.requests || []))
      .catch(() => setResetReqs([]));
  };
  useMemEffect(() => { if (wsId) load(wsId); }, [wsId]);

  const pickable = (me && me.memberships) || [];
  // ORG-01: management seats are team_lead + admin (admin = the multi-workspace geo manager).
  const managed = pickable.filter((m) => m.role === 'team_lead' || m.role === 'admin');
  const canManage = !!(me && (me.orgTier != null || managed.length > 0));
  const scopeNote = !me ? '…'
    : me.orgTier === 'product_manager' ? 'org-wide · audited'
    : managed.some((m) => m.role === 'admin') ? ('the workspace' + (managed.length === 1 ? '' : 's') + ' you administer')
    : managed.length > 0 ? ('the team' + (managed.length === 1 ? '' : 's') + ' you lead')
    : 'read-only for your role';

  const members = (data && data.members) || [];

  const mutate = async (userId, fn) => {
    setBusy(userId); setErr(null);
    try { await fn(); load(wsId); }
    catch (e) { setErr(e.message); setBusy(null); }
  };
  const onSeatRole = (userId, seatRole) =>
    mutate(userId, () => memFetch('/' + encodeURIComponent(userId) + '/seat-role', { method: 'PATCH', body: { workspaceId: wsId, seatRole } }));
  const onActive = (userId, active) =>
    mutate(userId, () => memFetch('/' + encodeURIComponent(userId) + '/active', { method: 'PATCH', body: { workspaceId: wsId, active } }));
  // AUTH-02: issue a NEW one-time password (shown once, like onboarding); the target's sessions are revoked server-side
  const onResetPw = (m) => mutate(m.userId, async () => {
    const r = await memFetch('/' + encodeURIComponent(m.userId) + '/reset-password', { method: 'POST', body: { workspaceId: wsId } });
    setToast({ name: m.name, email: m.email, tempPassword: r.tempPassword, reset: true });
  });

  return (
    <div className="members-admin">
      <div className="row between wrap" style={{ marginBottom: 14, gap: 10 }}>
        <div>
          <div className="sec-title">Members &amp; access</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {members.filter((m) => m.isActive).length} active · you manage {scopeNote}
          </div>
        </div>
        {canManage && wsId && <button className="btn btn-primary" onClick={() => setAdding(true)}><I.add size={15} /> Add member</button>}
      </div>

      {/* The API is per-workspace; a lead/PM/admin who belongs to several picks one.
          TODO RBAC-07: an org-tier viewer should pick from ALL workspaces, not just memberships. */}
      {pickable.length > 1 && (
        <div className="row gap-6 wrap" style={{ marginBottom: 12 }}>
          {pickable.map((m) => (
            <button key={m.workspaceId} className={'tab' + (wsId === m.workspaceId ? ' active' : '')} onClick={() => setWsId(m.workspaceId)}>
              {m.name || m.workspaceId.slice(0, 8)}
            </button>
          ))}
        </div>
      )}

      {err && <div className="muted" style={{ color: 'var(--fail)', fontSize: 12.5, marginBottom: 10 }} role="alert">{err}</div>}

      {/* AUTH-02: the forgot-password queue — no email provider here, so a manager issues the one-time password */}
      {canManage && resetReqs.length > 0 && (
        <div className="card card-pad" style={{ marginBottom: 12, padding: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{resetReqs.length} password reset {resetReqs.length === 1 ? 'request' : 'requests'}</div>
          {resetReqs.map((r) => (
            <div key={r.id} className="row between wrap" style={{ gap: 8, alignItems: 'center', padding: '6px 0' }}>
              <span style={{ fontSize: 12.5 }}><b>{r.name}</b> <span className="muted">{r.email}</span></span>
              <button className="btn btn-quiet" disabled={busy === r.userId} onClick={() => onResetPw({ userId: r.userId, name: r.name, email: r.email })}>Issue new password</button>
            </div>
          ))}
        </div>
      )}

      <div className="mb-table">
        <div className="mb-row mb-head"><div>Member</div><div>Seat role</div><div>Status</div><div></div></div>
        {loading
          ? <div className="muted" style={{ padding: 14, fontSize: 12.5 }}>Loading…</div>
          : members.length === 0
            ? <div className="muted" style={{ padding: 14, fontSize: 12.5 }}>No members in this workspace.</div>
            : members.map((m) => (
              <div key={m.userId} className={'mb-row' + (m.isActive ? '' : ' inactive')}>
                <div className="mb-person">
                  <span className="mini-av" style={{ width: 30, height: 30, fontSize: 11 }}>{m.initials}</span>
                  <div style={{ minWidth: 0 }}><div className="nm">{m.name}</div><div className="em">{m.email}</div></div>
                </div>
                <div>
                  {m.orgTier
                    ? <span className="role-chip rc-pm">Product mgr</span>
                    : (m.canManage
                        ? <select className="mb-select" value={m.seatRole} disabled={busy === m.userId} onChange={(e) => onSeatRole(m.userId, e.target.value)}>
                            {/* ORG-01 grant ceiling mirrored: a team_lead viewer may only set sme/dev
                                (the server's member_grant_ceiling_ok enforces regardless). */}
                            {data && data.viewerScope !== 'team_lead' && <option value="admin">Admin</option>}
                            {data && data.viewerScope !== 'team_lead' && <option value="team_lead">Team lead</option>}
                            <option value="sme">SME</option>
                            <option value="dev">Dev</option>
                          </select>
                        : <span className={'role-chip ' + seatChipClass(m.seatRole)}>{SEAT_LABEL[m.seatRole] || m.seatRole}</span>)}
                </div>
                <div>{m.isActive ? <span className="mb-status on">Active</span> : <span className="mb-status off">Deactivated</span>}</div>
                <div className="mb-actions">
                  {m.canManage && !m.orgTier && (
                    m.isActive
                      ? <React.Fragment>
                          <button className="mb-link" disabled={busy === m.userId} onClick={() => onResetPw(m)} title="Issue a new one-time password (their sessions are signed out)">Reset password</button>
                          <button className="mb-link danger" disabled={busy === m.userId} onClick={() => onActive(m.userId, false)}>Deactivate</button>
                        </React.Fragment>
                      : <button className="mb-link" disabled={busy === m.userId} onClick={() => onActive(m.userId, true)}>Reactivate</button>
                  )}
                </div>
              </div>
            ))}
      </div>

      {/* ORG-02: TL/admin/PM assign clients to the sme/dev seats. The /api/assignments
          API is ACTIVE-workspace scoped (session), so the panel only manages the active
          workspace — an honest hint replaces it when another tab is picked. */}
      {canManage && !loading && members.length > 0 && (
        wsId === (me && me.activeWorkspaceId)
          ? <ClientAssignments members={members} />
          : <p className="muted" style={{ fontSize: 12, marginTop: 14 }}><I.lock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />Client assignments are managed in your <b>active</b> workspace — switch workspaces (sidebar) to manage this one's.</p>
      )}

      {adding && <AddMemberModal workspaceId={wsId} viewerScope={data && data.viewerScope} onClose={() => setAdding(false)} onAdded={(m) => { setAdding(false); if (m.tempPassword) setToast(m); load(wsId); }} />}
      {toast && <TempPasswordToast member={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

/* ORG-02 panel: per sme/dev member — active-assignment chips (× revokes) + a picker
   to grant from the workspace KB clients. Authority is the SERVER's (assign_clients
   admit + target-seat rule in routes/assignments.js); this is presentation. */
function ClientAssignments({ members }) {
  const [rows, setRows] = useMemState(null);       // /api/assignments list (all members)
  const [err, setErr] = useMemState(null);
  const [busy, setBusy] = useMemState(null);       // `${userId}:${clientSlug}` mid-mutation
  const clients = (window.MAV && window.MAV.clients) || [];

  const load = () => assnFetch('')
    .then((d) => { setRows(d.assignments || []); setErr(null); })
    .catch((e) => setErr(e.message));
  useMemEffect(() => { load(); }, []);

  const scoped = members.filter((m) => m.isActive && !m.orgTier && (m.seatRole === 'sme' || m.seatRole === 'dev'));
  const activeFor = (userId) => (rows || []).filter((a) => a.userId === userId && a.isActive);

  const mutate = async (key, fn) => {
    setBusy(key); setErr(null);
    try { await fn(); await load(); } catch (e) { setErr(e.message); }
    setBusy(null);
  };
  const grant = (userId, clientSlug) =>
    mutate(userId + ':' + clientSlug, () => assnFetch('', { method: 'POST', body: { client_slug: clientSlug, user_id: userId } }));
  const revoke = (userId, clientSlug) =>
    mutate(userId + ':' + clientSlug, () => assnFetch('/' + encodeURIComponent(clientSlug) + '/' + encodeURIComponent(userId), { method: 'DELETE' }));

  return (
    <div style={{ marginTop: 22 }}>
      <div className="sec-title" style={{ fontSize: 13.5 }}>Client assignments</div>
      <div className="muted" style={{ fontSize: 12, margin: '2px 0 10px' }}>
        SMEs and Devs can only build for clients assigned here (the server enforces at start).
      </div>
      {err && <div className="muted" style={{ color: 'var(--fail)', fontSize: 12.5, marginBottom: 8 }} role="alert">{err}</div>}
      {rows === null
        ? <div className="muted" style={{ fontSize: 12.5 }}>Loading…</div>
        : scoped.length === 0
          ? <div className="muted" style={{ fontSize: 12.5 }}>No SME/Dev seats in this workspace.</div>
          : scoped.map((m) => {
            const mine = activeFor(m.userId);
            const assignedSlugs = mine.map((a) => a.clientSlug);
            const grantable = clients.filter((c) => !assignedSlugs.includes(c.slug));
            return (
              <div key={m.userId} className="row wrap" style={{ gap: 8, alignItems: 'center', padding: '7px 0', borderTop: '1px solid var(--line)' }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, minWidth: 130 }}>{m.name}</span>
                <span className={'role-chip ' + seatChipClass(m.seatRole)} style={{ flexShrink: 0 }}>{SEAT_LABEL[m.seatRole]}</span>
                {mine.length === 0 && <span className="muted" style={{ fontSize: 11.5 }}>no clients assigned</span>}
                {mine.map((a) => (
                  <span key={a.clientSlug} className="role-chip rc-builder" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {a.clientName || a.clientSlug}
                    <button className="mb-link danger" style={{ padding: 0, lineHeight: 1 }} title={'Revoke ' + (a.clientName || a.clientSlug)}
                      disabled={busy === m.userId + ':' + a.clientSlug} onClick={() => revoke(m.userId, a.clientSlug)}>×</button>
                  </span>
                ))}
                {grantable.length > 0 && (
                  <select className="mb-select" value="" disabled={!!busy}
                    onChange={(e) => { if (e.target.value) grant(m.userId, e.target.value); }}>
                    <option value="" disabled>+ assign…</option>
                    {grantable.map((c) => <option key={c.slug} value={c.slug}>{c.name || c.slug}</option>)}
                  </select>
                )}
              </div>
            );
          })}
    </div>
  );
}

function AddMemberModal({ workspaceId, viewerScope, onClose, onAdded }) {
  const [name, setName] = useMemState('');
  const [email, setEmail] = useMemState('');
  const [role, setRole] = useMemState('dev');
  // ORG-01 grant ceiling mirrored (same rule as the row select): a team_lead viewer
  // may only grant sme/dev — the server's member_grant_ceiling_ok enforces regardless.
  const seatOpts = viewerScope === 'team_lead'
    ? [['dev', 'Dev'], ['sme', 'SME']]
    : [['dev', 'Dev'], ['sme', 'SME'], ['team_lead', 'Team lead'], ['admin', 'Admin']];
  const [busy, setBusy] = useMemState(false);
  const [err, setErr] = useMemState(null);
  const valid = name.trim().length > 1 && /\S+@\S+\.\S+/.test(email.trim()) && workspaceId;
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await memFetch('', { method: 'POST', body: { workspaceId, email: email.trim(), name: name.trim(), seatRole: role } });
      onAdded({ name: name.trim(), email: email.trim(), tempPassword: r.tempPassword, created: r.created, reactivated: r.reactivated });
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal add-member" onClick={(e) => e.stopPropagation()}>
        <div className="fb-modal-h">
          <div><div style={{ fontSize: 16, fontWeight: 700 }}>Add member</div><div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>Invites a teammate to this workspace. A one-time password is issued for new accounts.</div></div>
          <button className="icon-btn" onClick={onClose}><I.x size={16} /></button>
        </div>
        <div className="add-member-body">
          <label className="field-lbl">Full name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Alex Mercer" autoFocus />
          <label className="field-lbl" style={{ marginTop: 12 }}>Email</label>
          <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@company.com" />
          <label className="field-lbl" style={{ marginTop: 12 }}>Seat role</label>
          <div className={'seg seg-' + seatOpts.length}>
            {seatOpts.map(([id, l]) => (
              <button type="button" key={id} className={'seg-opt' + (role === id ? ' sel' : '')} onClick={() => setRole(id)}>
                <div className="sl">{l}</div>
              </button>
            ))}
          </div>
          <p className="muted" style={{ fontSize: 11.5, marginTop: 12 }}><I.lock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />The PM org tier is assigned separately. Team leads may grant SME/Dev seats only (the server enforces). An existing user is added to this workspace at the chosen seat.</p>
          {err && <p style={{ color: 'var(--fail)', fontSize: 12, marginTop: 8 }} role="alert">{err}</p>}
        </div>
        <div className="add-member-foot">
          <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid || busy} onClick={submit}>{busy ? <span className="spinner" /> : <I.add size={15} />} Add member</button>
        </div>
      </div>
    </div>
  );
}

function TempPasswordToast({ member, onClose }) {
  return (
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal temp-pw" onClick={(e) => e.stopPropagation()}>
        <div style={{ textAlign: 'center', padding: '8px 4px' }}>
          <div className="tp-ico"><I.check size={26} /></div>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 10 }}>{member.reset ? 'New one-time password for ' + member.name : member.name + ' added'}</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>Share this one-time password securely — they change it under Account → Security after signing in.{member.reset ? ' Their previous sessions are signed out.' : ''}</div>
          <div className="tp-pw mono">{member.tempPassword}</div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { MembersAdmin, AddMemberModal, TempPasswordToast });
